import React, {useMemo, forwardRef} from "react";
import {
    Dialog, DialogContent, DialogActions, Typography, Button, Stack, Chip, Grid,
    Box, Avatar, IconButton, Divider, Tooltip, Slide, useTheme
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TodayIcon from "@mui/icons-material/Today";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import "dayjs/locale/fr";

dayjs.locale("fr");

const Transition = forwardRef(function Transition(props, ref) {
    return (
        <Slide direction="up" ref={ref} {...props}>
            {props.children}
        </Slide>
    );
});

const statusConfig = (statut = "") => {
    const s = (statut || "").toLowerCase().trim();
    if (s === "livrée" || s === "livree") return {color: "success", label: "livrée", Icon: CheckCircleIcon};
    if (s === "refusée" || s === "refusee") return {color: "error", label: "refusée", Icon: HighlightOffIcon};
    return {color: "warning", label: "en attente", Icon: HourglassBottomIcon};
};

const FancyField = ({icon: Icon, label, value, hint}) => {
    const theme = useTheme();
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.06),
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`
            }}
        >
            <Avatar
                variant="rounded"
                sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main
                }}
            >
                <Icon fontSize="small"/>
            </Avatar>
            <Box flex={1} minWidth={0}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body1" noWrap>{value ?? "—"}</Typography>
                {hint ? <Typography variant="caption" color="text.disabled">{hint}</Typography> : null}
            </Box>
        </Stack>
    );
};

const norm = (s = "") => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const DemandeDetailsDialog = ({open, onClose, demande}) => {
    const theme = useTheme();
    const status = statusConfig(demande?.statut);

    const dates = useMemo(() => {
        const d = {
            demandeAt: demande?.dateDemande ? dayjs(demande.dateDemande) : null,
            start: demande?.dateDebut ? dayjs(demande.dateDebut) : null,
            end: demande?.dateFin ? dayjs(demande.dateFin) : null
        };
        const durationDays = d.start && d.end ? Math.max(0, d.end.diff(d.start, "day") + 1) : null;
        return {...d, durationDays};
    }, [demande?.dateDemande, demande?.dateDebut, demande?.dateFin]);

    const isAttestation = (demande?.type || "").toLowerCase().includes("attest");

    const isConge = norm(demande?.type || "").includes("conge");
    const isLivree = norm(demande?.statut || "") === "livree";
    const statusLabel = isConge && isLivree ? "Accepté" : status.label;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionComponent={Transition}
            slotProps={{ paper: {
                sx: {
                    borderRadius: 4,
                    overflow: "hidden",
                    backdropFilter: "blur(8px)",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.25)"
                }
            } }}
        >
            <Box
                sx={{
                    position: "relative",
                    px: 3, pt: 3, pb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: "#fff"
                }}
            >
                <Box sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(1200px 300px at 20% -20%, rgba(255,255,255,0.25), transparent), radial-gradient(600px 200px at 110% 0%, rgba(255,255,255,0.18), transparent)",
                    pointerEvents: "none"
                }}/>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar variant="rounded"
                                sx={{width: 44, height: 44, bgcolor: alpha("#fff", 0.2), color: "#fff"}}>
                            <AssignmentTurnedInIcon/>
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{m: 0}}>Détail de la demande</Typography>
                            <Typography variant="body2" sx={{opacity: 0.9}}>
                                {dates.demandeAt ? dates.demandeAt.format("DD/MM/YYYY à HH:mm") : "—"}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip
                            icon={<status.Icon/>} label={statusLabel} color={status.color}
                            sx={{
                                bgcolor: alpha(theme.palette[status.color].light, 0.25),
                                color: "#fff", "& .MuiChip-icon": {color: "#fff"}, fontWeight: 600
                            }}
                        />
                        <IconButton onClick={onClose} sx={{color: "#fff"}}><CloseRoundedIcon/></IconButton>
                    </Stack>
                </Stack>
            </Box>

            <DialogContent dividers sx={{p: 3, bgcolor: alpha(theme.palette.background.default, 0.5)}}>
                {demande ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FancyField icon={AssignmentTurnedInIcon} label="Type" value={demande.type}/>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FancyField icon={CategoryOutlinedIcon} label="Sous-type" value={demande.sousType}/>
                        </Grid>

                        <Grid item xs={12}><Divider sx={{my: 0.5}}/></Grid>

                        <Grid item xs={12} md={6}>
                            <FancyField
                                icon={EventOutlinedIcon}
                                label="Date de la demande"
                                value={dates.demandeAt ? dates.demandeAt.format("DD/MM/YYYY à HH:mm") : "—"}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FancyField
                                icon={HourglassBottomIcon}
                                label="Statut"
                                value={
                                    <Chip
                                        size="small"
                                        icon={<status.Icon/>}
                                        label={statusLabel}
                                        color={status.color}
                                        sx={{
                                            fontWeight: 600,
                                            bgcolor: alpha(theme.palette[status.color].main, 0.12),
                                            color: theme.palette[status.color].main,
                                            "& .MuiChip-icon": {color: theme.palette[status.color].main}
                                        }}
                                    />
                                }
                            />
                        </Grid>

                        {!isAttestation && (
                            <>
                                <Grid item xs={12} md={4}>
                                    <FancyField icon={CalendarMonthIcon} label="Date début"
                                                value={dates.start ? dates.start.format("DD/MM/YYYY") : "—"}/>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FancyField icon={TodayIcon} label="Date fin"
                                                value={dates.end ? dates.end.format("DD/MM/YYYY") : "—"}/>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FancyField
                                        icon={InfoOutlinedIcon}
                                        label="Durée"
                                        value={
                                            dates.durationDays != null ? (
                                                <Tooltip title="Calcul inclusif (du 1er au dernier jour)">
                                                    <Box component="span">{dates.durationDays} jour(s)</Box>
                                                </Tooltip>
                                            ) : "—"
                                        }
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                ) : (
                    <Box sx={{py: 6, textAlign: "center"}}>
                        <Typography variant="body1" color="text.secondary">Aucune donnée à afficher.</Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2, bgcolor: alpha(theme.palette.background.paper, 0.6)}}>
                <Button onClick={onClose} variant="contained" sx={{borderRadius: 2}}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DemandeDetailsDialog;